const MS_WEEK = 7 * 24 * 60 * 60 * 1000;
const MS_DAY = 24 * 60 * 60 * 1000;

export function parseISODate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function formatISODate(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Fecha ISO (yyyy-mm-dd) → dd-mm-yyyy para mostrar en UI */
export function formatDisplayDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-');
  if (!y || !m || !d) return iso;
  return `${d.padStart(2, '0')}-${m.padStart(2, '0')}-${y}`;
}

export function getAbsoluteWeekIndex(birthDate, targetDate) {
  if (!birthDate || !targetDate) return -1;
  const diff = targetDate.getTime() - birthDate.getTime();
  if (diff < 0) return -1;
  return Math.floor(diff / MS_WEEK);
}

export function getWeekStartDate(birthDate, absoluteWeekIndex) {
  if (!birthDate || absoluteWeekIndex < 0) return null;
  return new Date(birthDate.getTime() + absoluteWeekIndex * MS_WEEK);
}

export function getPeriodForWeek(periods, weekStartDate) {
  if (!periods?.length) return null;
  let active = periods[0];
  const weekStr = formatISODate(weekStartDate);
  for (const p of periods) {
    if (!p.startDate) {
      active = p;
      continue;
    }
    if (p.startDate <= weekStr) {
      active = p;
    }
  }
  return active;
}

/** Texto por defecto en la casilla de cumpleaños, como weeks.ginatrapani.org */
export function defaultBirthdayHeadline(age, calendarYear) {
  return `🎂 ${age} en ${calendarYear}`;
}

/**
 * Construye el flujo de casillas (cumpleaños → semanas → eventos por día),
 * igual que el generador Hugo de life-in-weeks.
 */
export function buildLifeBoxStream(birthDate, expectancyYears, periods, entries, today = new Date()) {
  if (!birthDate || !expectancyYears) {
    return { boxes: [], decades: [] };
  }

  const entriesByDate = {};
  (entries || []).forEach((e) => {
    if (e?.date) entriesByDate[e.date] = e;
  });

  const birthYear = birthDate.getFullYear();
  const currentAbs = getAbsoluteWeekIndex(birthDate, today);

  let stateLiving = periods?.[0]?.livingId;
  let stateDoing = periods?.[0]?.doingId;

  const boxes = [];
  const decades = [];

  function syncStateFromPeriod(dateISO) {
    const d = parseISODate(dateISO);
    const p = getPeriodForWeek(periods, d);
    if (p) {
      stateLiving = p.livingId;
      stateDoing = p.doingId;
    }
  }

  function syncStateFromEntry(entry) {
    if (!entry) return;
    if (entry.livingId) stateLiving = entry.livingId;
    if (entry.doingId) stateDoing = entry.doingId;
  }

  for (let calendarYear = birthYear; calendarYear < birthYear + expectancyYears; calendarYear++) {
    const age = calendarYear - birthYear;

    if (age > 0 && age % 10 === 0) {
      decades.push({ age, calendarYear, boxIndex: boxes.length });
    }

    const anniversary = new Date(calendarYear, birthDate.getMonth(), birthDate.getDate());
    const anniversaryISO = formatISODate(anniversary);
    const nextAnniversary = new Date(calendarYear + 1, birthDate.getMonth(), birthDate.getDate());

    syncStateFromPeriod(anniversaryISO);

    const birthdayEntry = entriesByDate[anniversaryISO];
    if (birthdayEntry) syncStateFromEntry(birthdayEntry);

    const absBirth = getAbsoluteWeekIndex(birthDate, anniversary);
    boxes.push({
      key: `birthday-${anniversaryISO}`,
      type: 'birthday',
      date: anniversaryISO,
      age,
      calendarYear,
      headline: birthdayEntry?.headline?.trim() || defaultBirthdayHeadline(age, calendarYear),
      description: birthdayEntry?.description || '',
      livingId: stateLiving,
      doingId: stateDoing,
      inFuture: absBirth > currentAbs,
      isCurrent: absBirth === currentAbs
    });

    if (birthdayEntry) syncStateFromEntry(birthdayEntry);

    for (let week = 1; week <= 52; week++) {
      const weekStart = new Date(anniversary.getTime() + week * MS_WEEK);
      if (weekStart >= nextAnniversary) break;

      const weekISO = formatISODate(weekStart);
      syncStateFromPeriod(weekISO);

      const absWeek = getAbsoluteWeekIndex(birthDate, weekStart);
      const inFuture = absWeek > currentAbs;
      const isCurrent = absWeek === currentAbs;

      const weekEntry = entriesByDate[weekISO];
      const weekHeadline = weekEntry?.headline?.trim() || '';

      boxes.push({
        key: `week-${weekISO}`,
        type: 'week',
        date: weekISO,
        headline: weekHeadline,
        description: weekEntry?.description || '',
        livingId: stateLiving,
        doingId: stateDoing,
        inFuture,
        isCurrent
      });

      if (weekEntry) syncStateFromEntry(weekEntry);

      for (let day = 0; day < 7; day++) {
        const dayDate = new Date(weekStart.getTime() + day * MS_DAY);
        if (dayDate >= nextAnniversary) break;

        const dayISO = formatISODate(dayDate);
        // El inicio de semana ya tiene casilla "week"; no duplicar como evento del mismo día
        if (dayISO === weekISO) {
          const anchorEntry = entriesByDate[dayISO];
          if (anchorEntry) syncStateFromEntry(anchorEntry);
          continue;
        }
        const dayEntry = entriesByDate[dayISO];
        if (!dayEntry?.headline?.trim()) {
          if (dayEntry) syncStateFromEntry(dayEntry);
          continue;
        }

        syncStateFromEntry(dayEntry);
        const absDay = getAbsoluteWeekIndex(birthDate, dayDate);

        boxes.push({
          key: `event-${dayISO}`,
          type: 'event',
          date: dayISO,
          headline: dayEntry.headline.trim(),
          description: dayEntry.description || '',
          livingId: stateLiving,
          doingId: stateDoing,
          inFuture: absDay > currentAbs,
          isCurrent: absDay === currentAbs
        });
      }
    }
  }

  return { boxes, decades };
}

function mapEntryRow(e) {
  return {
    id: e.id || `e-${e.date}`,
    date: e.date,
    headline: e.headline || '',
    description: e.description || '',
    livingId: e.livingId || null,
    doingId: e.doingId || null
  };
}

/** Migra events[] antiguos a entries[] (una sola entrada por fecha) */
export function entriesFromLegacy(data) {
  const list = Array.isArray(data.entries) && data.entries.length
    ? data.entries
    : (data.events || []);
  const byDate = new Map();
  list.forEach((e) => {
    if (!e?.date) return;
    byDate.set(e.date, mapEntryRow(e));
  });
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export function upsertEntry(entries, patch) {
  const list = [...entries];
  const idx = list.findIndex((e) => e.date === patch.date);
  const row = {
    id: patch.id || (idx >= 0 ? list[idx].id : `e-${patch.date}-${Date.now()}`),
    date: patch.date,
    headline: patch.headline ?? (idx >= 0 ? list[idx].headline : ''),
    description: patch.description ?? (idx >= 0 ? list[idx].description : ''),
    livingId: patch.livingId ?? (idx >= 0 ? list[idx].livingId : null),
    doingId: patch.doingId ?? (idx >= 0 ? list[idx].doingId : null)
  };
  if (!row.headline?.trim() && !row.description?.trim() && !row.livingId && !row.doingId) {
    if (idx >= 0) list.splice(idx, 1);
    return list;
  }
  if (idx >= 0) list[idx] = row;
  else list.push(row);
  return list.sort((a, b) => a.date.localeCompare(b.date));
}
